import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth/useAuth";
import {
  createApprovalsClient,
  type ActivityItem,
  type ApprovalItem,
} from "@/lib/api/approvalsApi";
import { siguientePagina } from "@/lib/approvals/paginacion";
import { shortSummary } from "@/lib/approvals/summary";
import {
  ensureNotificationPermission,
  notificationPermission,
  showNotification,
  type ShowNotificationOptions,
} from "@/lib/notifications";

const POLL_MS = 5000;
const MAX_FAILURES = 3;
// El poll de pendientes reemplaza toda la lista; subimos el limite a 200 (el
// backend clampa a 1..200). Si hay mas, mostramos un aviso (no paginamos).
const PENDING_LIMIT = 200;
// Tamano de pagina del scroll infinito de resueltas.
const RESOLVED_PAGE_SIZE = 50;
// Tamano de pagina del scroll infinito del registro de actividad.
const ACTIVITY_PAGE_SIZE = 50;

/** Callback opcional que el consumidor pasa para reaccionar al click de la notificacion. */
export interface ApprovalsInboxOptions {
  /** Se invoca (ademas de enfocar la ventana) al hacer click en la notificacion. */
  onNotificationClick?: (item: ApprovalItem) => void;
}

/**
 * Hook con la bandeja de aprobaciones HITL: polling de pendientes, resolucion y
 * optimistic update. Extraido de la antigua ApprovalsPage para reusarlo desde la
 * home (sidebar + panel ancho). El backend ya filtra /approvals por permiso de
 * agente, asi que aqui solo listamos lo que el usuario puede atender.
 *
 * El polling NUNCA se mata: tras N fallos marca `connectionLost` (banner no
 * bloqueante) y sigue reintentando; un exito lo limpia.
 */
export function useApprovalsInbox(options?: ApprovalsInboxOptions) {
  const { getToken, logout } = useAuth();
  const { t } = useTranslation();
  const api = useMemo(() => createApprovalsClient(getToken, logout), [getToken, logout]);

  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionLost, setConnectionLost] = useState(false);
  // canApprove: el usuario tiene permiso a algun agente HITL -> la pestana
  // Aprobaciones debe estar SIEMPRE presente (aunque 0 pendientes).
  const [canApprove, setCanApprove] = useState(false);
  // Agentes HITL que el usuario puede atender: grupos FIJOS del arbol de la
  // bandeja. Sin esto, un agente sin pendientes no se pinta y sus resueltas
  // quedan inaccesibles (no hay donde pulsar la pestana Resueltas).
  const [hitlAgents, setHitlAgents] = useState<string[]>([]);
  // Hay MAS pendientes de las que el poll trae (limite 200). No paginamos por
  // scroll — solo mostramos un aviso discreto al final de la lista de pendientes.
  const [pendingHasMore, setPendingHasMore] = useState(false);
  const firstLoad = useRef(true);

  // Estado del permiso de notificaciones, para que la UI pueda reflejarlo.
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>(
    () => notificationPermission(),
  );

  // IDs ya vistos: en el primer poll se llena SIN notificar (son pendientes
  // preexistentes, no novedades). En polls sucesivos, cualquier correlation_id
  // ausente aqui es una aprobacion nueva -> notificamos. Es un ref para no
  // re-disparar el efecto de polling cuando cambia.
  const seenIds = useRef<Set<string>>(new Set());

  // onNotificationClick en un ref: el consumidor puede pasar una closure nueva
  // en cada render; no queremos reiniciar el polling por ello.
  const onClickRef = useRef(options?.onNotificationClick);
  onClickRef.current = options?.onNotificationClick;

  /** Pide permiso de notificaciones (tras gesto del usuario) y refresca el estado local. */
  const requestNotificationPermission = useCallback(async () => {
    const perm = await ensureNotificationPermission();
    setNotifPermission(perm);
    return perm;
  }, []);

  /** Construye y muestra una notificacion para una aprobacion nueva. */
  const notifyNewApproval = useCallback(
    (item: ApprovalItem) => {
      const agentLabel = t(`agents.${item.assistant_id}.label`, item.assistant_id);
      const { headline, detail } = shortSummary(item);
      const bodyParts = [headline, detail].filter((s) => s.length > 0);
      const opts: ShowNotificationOptions = {
        body: bodyParts.join(" · ") || undefined,
        // Un tag por aprobacion: si el mismo item se re-notifica, se colapsa.
        tag: `approval-${item.correlation_id}`,
        onClick: () => onClickRef.current?.(item),
      };
      showNotification(t("approvals.notification.title", { agent: agentLabel }), opts);
    },
    [t],
  );

  // notifyNewApproval en un ref: lo usa el efecto de polling (deps [api]) sin
  // que un cambio de idioma reinicie el intervalo.
  const notifyRef = useRef(notifyNewApproval);
  notifyRef.current = notifyNewApproval;


  useEffect(() => {
    let failures = 0;
    let active = true;

    // El sidebar solo muestra aprobaciones PENDING (accionables). El endpoint
    // /approvals?status=pending incluye tambien las 'processing' (en curso tras
    // aprobar), asi que las filtramos aqui: en cuanto el usuario aprueba, la
    // aprobacion desaparece de la bandeja. Si el resume falla, el backend la
    // revierte a 'pending' y el proximo poll la vuelve a mostrar.
    const fetchPending = () =>
      api
        .listApprovals({ status: "pending", limit: PENDING_LIMIT })
        .then((data) => {
          if (!active) return;
          const pending = data.approvals.filter((it) => it.status === "pending");
          setPendingHasMore(data.has_more ?? false);

          // Deteccion de novedades para notificar. En la PRIMERA carga solo
          // sembramos seenIds (los pendientes que ya existian no son novedad).
          // A partir de ahi, cualquier id no visto dispara una notificacion.
          if (firstLoad.current) {
            for (const it of pending) seenIds.current.add(it.correlation_id);
          } else {
            for (const it of pending) {
              if (!seenIds.current.has(it.correlation_id)) {
                seenIds.current.add(it.correlation_id);
                notifyRef.current(it);
              }
            }
          }
          // No dejamos crecer seenIds indefinidamente: lo recortamos a los ids
          // que siguen pendientes (los resueltos ya no volveran como novedad).
          seenIds.current = new Set(pending.map((it) => it.correlation_id));

          setItems(pending);
          if (typeof data.can_approve === "boolean") setCanApprove(data.can_approve);
          if (Array.isArray(data.hitl_agents)) setHitlAgents(data.hitl_agents);
          failures = 0;
          setConnectionLost(false);
        })
        .catch((err) => {
          if (!active) return;
          failures++;
          if (failures >= MAX_FAILURES) setConnectionLost(true);
          console.error(err);
        })
        .finally(() => {
          if (active && firstLoad.current) {
            firstLoad.current = false;
            setLoading(false);
          }
        });

    fetchPending();
    const interval = setInterval(fetchPending, POLL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [api]);

  // Resueltas por agente, cargadas BAJO DEMANDA (al expandir el subgrupo). No entran
  // en el polling: son histórico y cambian poco. Estado por agentId.
  const [resolvedByAgent, setResolvedByAgent] = useState<Record<string, ApprovalItem[]>>({});
  const [resolvedLoading, setResolvedLoading] = useState<Record<string, boolean>>({});
  // Offset acumulado por agente (cuantas resueltas ya tenemos) y si hay mas paginas.
  const [resolvedOffset, setResolvedOffset] = useState<Record<string, number>>({});
  const [resolvedHasMore, setResolvedHasMore] = useState<Record<string, boolean>>({});
  // Guard sincrono contra dobles disparos del IntersectionObserver antes de que
  // el setState de `resolvedLoading` se refleje. El ref evita reentradas en el
  // mismo frame que el flag de estado (async) no atraparia.
  const resolvedInFlight = useRef<Set<string>>(new Set());

  // La aprobacion seleccionada puede estar en `items` (pendientes) O en las resueltas
  // cargadas (`resolvedByAgent`), no solo en pendientes: al seleccionar una resuelta,
  // el detalle debe encontrarla aqui (si no, `item` llega null y el panel sale vacio).
  const selected = useMemo(() => {
    const inPending = items.find((i) => i.correlation_id === selectedId);
    if (inPending) return inPending;
    for (const list of Object.values(resolvedByAgent)) {
      const found = list.find((i) => i.correlation_id === selectedId);
      if (found) return found;
    }
    return null;
  }, [items, resolvedByAgent, selectedId]);

  // Primera pagina de resueltas de un agente (offset 0). RESETEA el acumulado:
  // se usa al abrir la pestana Resueltas y al refrescar tras resolver algo.
  const loadResolved = useCallback(
    async (agentId: string) => {
      if (resolvedInFlight.current.has(agentId)) return;
      resolvedInFlight.current.add(agentId);
      setResolvedLoading((p) => ({ ...p, [agentId]: true }));
      try {
        const data = await api.listApprovals({
          status: "resolved",
          assistant_id: agentId,
          limit: RESOLVED_PAGE_SIZE,
          offset: 0,
        });
        const page = data.approvals.filter((it) => it.status === "resolved");
        setResolvedByAgent((p) => ({ ...p, [agentId]: page }));
        setResolvedOffset((p) => ({ ...p, [agentId]: page.length }));
        setResolvedHasMore((p) => ({ ...p, [agentId]: data.has_more ?? false }));
      } catch (err) {
        console.error(err);
      } finally {
        resolvedInFlight.current.delete(agentId);
        setResolvedLoading((p) => ({ ...p, [agentId]: false }));
      }
    },
    [api],
  );

  // Siguiente pagina de resueltas (APPEND). Guard: ignora si ya carga o no hay mas.
  const loadMoreResolved = useCallback(
    async (agentId: string) => {
      if (resolvedInFlight.current.has(agentId)) return;
      if (resolvedHasMore[agentId] === false) return;
      resolvedInFlight.current.add(agentId);
      setResolvedLoading((p) => ({ ...p, [agentId]: true }));
      try {
        const offset = resolvedOffset[agentId] ?? 0;
        const data = await api.listApprovals({
          status: "resolved",
          assistant_id: agentId,
          limit: RESOLVED_PAGE_SIZE,
          offset,
        });
        const page = data.approvals.filter((it) => it.status === "resolved");
        setResolvedByAgent((p) => ({ ...p, [agentId]: [...(p[agentId] ?? []), ...page] }));
        setResolvedOffset((p) => ({ ...p, [agentId]: (p[agentId] ?? 0) + page.length }));
        setResolvedHasMore((p) => ({ ...p, [agentId]: data.has_more ?? false }));
      } catch (err) {
        console.error(err);
      } finally {
        resolvedInFlight.current.delete(agentId);
        setResolvedLoading((p) => ({ ...p, [agentId]: false }));
      }
    },
    [api, resolvedHasMore, resolvedOffset],
  );

  // Registro de actividad por agente, bajo demanda (no entra en el polling).
  const [activityByAgent, setActivityByAgent] = useState<Record<string, ActivityItem[]>>({});
  const [activityLoading, setActivityLoading] = useState<Record<string, boolean>>({});
  // Offset acumulado por agente (cuantas entradas ya tenemos) y si hay mas paginas.
  // Mismo patron que las resueltas: el registro se pagina por scroll infinito.
  const [activityOffset, setActivityOffset] = useState<Record<string, number>>({});
  const [activityHasMore, setActivityHasMore] = useState<Record<string, boolean>>({});
  const activityInFlight = useRef<Set<string>>(new Set());

  const loadActivity = useCallback(
    async (agentId: string) => {
      if (activityInFlight.current.has(agentId)) return;
      activityInFlight.current.add(agentId);
      setActivityLoading((p) => ({ ...p, [agentId]: true }));
      try {
        const data = await api.listActivity({
          assistant_id: agentId,
          limit: ACTIVITY_PAGE_SIZE,
          offset: 0,
        });
        setActivityByAgent((p) => ({ ...p, [agentId]: data.activity }));
        setActivityOffset((p) => ({ ...p, [agentId]: data.activity.length }));
        setActivityHasMore((p) => ({ ...p, [agentId]: !!data.has_more }));
      } catch (err) {
        console.error(err);
        // Lista vacia en vez de undefined: evita reintentos infinitos al re-abrir.
        setActivityByAgent((p) => ({ ...p, [agentId]: p[agentId] ?? [] }));
      } finally {
        activityInFlight.current.delete(agentId);
        setActivityLoading((p) => ({ ...p, [agentId]: false }));
      }
    },
    [api],
  );

  // Siguiente pagina del registro (APPEND). Guard: ignora si ya carga o no hay mas.
  // El `activityInFlight` (ref) es el guard SINCRONO contra los dobles disparos del
  // IntersectionObserver, que llegan antes de que el setState de loading se refleje.
  const loadMoreActivity = useCallback(
    async (agentId: string) => {
      if (activityInFlight.current.has(agentId)) return;
      if (activityHasMore[agentId] === false) return;
      activityInFlight.current.add(agentId);
      setActivityLoading((p) => ({ ...p, [agentId]: true }));
      try {
        const offset = activityOffset[agentId] ?? 0;
        const data = await api.listActivity({
          assistant_id: agentId,
          limit: ACTIVITY_PAGE_SIZE,
          offset,
        });
        // Forma FUNCIONAL en los tres setters: se acumula sobre el estado ACTUAL,
        // no sobre el que capturo el closure al crear el callback. El guard
        // `activityInFlight` se libera en el `finally`, ANTES de que React aplique
        // este append, asi que un re-disparo del sentinel en esa ventana leeria del
        // closure un array sin la pagina recien anadida y la perderia — mientras el
        // offset SI habria avanzado, dejando esas entradas inalcanzables para
        // siempre. Que es exactamente el fallo que este scroll viene a arreglar.
        setActivityByAgent((p) => ({
          ...p,
          [agentId]: siguientePagina(p[agentId] ?? [], offset, data.activity, !!data.has_more).items,
        }));
        setActivityOffset((p) => ({
          ...p,
          [agentId]: (p[agentId] ?? 0) + data.activity.length,
        }));
        setActivityHasMore((p) => ({ ...p, [agentId]: !!data.has_more }));
      } catch (err) {
        console.error(err);
      } finally {
        activityInFlight.current.delete(agentId);
        setActivityLoading((p) => ({ ...p, [agentId]: false }));
      }
    },
    [api, activityHasMore, activityOffset],
  );

  const resume = useCallback(
    (response: { type: string; args: unknown }) => {
      if (!selectedId) return Promise.reject(new Error("No selection"));
      return api.resumeApproval(selectedId, response);
    },
    [api, selectedId],
  );

  // Cierra la pendiente seleccionada como "reprocesada" y re-encola su documento
  // como run nuevo (el agente lo re-analiza con el codigo actual).
  const reprocess = useCallback(() => {
    if (!selectedId) return Promise.reject(new Error("No selection"));
    return api.reprocessApproval(selectedId);
  }, [api, selectedId]);

  // Tras un resume exitoso: UI OPTIMISTA. El resume es fire-and-forget (el backend
  // ya marco la fila 'processing' y la continuacion corre en background). La quitamos
  // del sidebar AL INSTANTE (aqui solo mostramos 'pending', y esta ya no lo esta) y
  // cerramos el detalle. Refrescamos contra el backend por si hubo otros cambios. Si
  // el resume fallara, el backend la revierte a 'pending' y el proximo poll la
  // re-mostraria. Patron estandar de bandeja de aprobaciones.
  const onResolved = useCallback(() => {
    // Capturamos el agente de la aprobacion recien resuelta ANTES de limpiar la
    // seleccion, para refrescar solo su lista de resueltas (no la de todos).
    const resolvedAgentId = selected?.assistant_id;
    setItems((prev) => prev.filter((it) => it.correlation_id !== selectedId));
    setSelectedId(null);
    api
      .listApprovals({ status: "pending", limit: PENDING_LIMIT })
      .then((data) => {
        setItems(data.approvals.filter((it) => it.status === "pending"));
        setPendingHasMore(!!data.has_more);
      })
      .catch((err) => console.error(err));
    // Si las resueltas de ese agente ya estaban cargadas, las refrescamos para que
    // la recien resuelta aparezca. Solo ese agente (no barremos todos).
    if (resolvedAgentId && resolvedByAgent[resolvedAgentId] !== undefined) {
      void loadResolved(resolvedAgentId);
    }
  }, [api, selectedId, selected, resolvedByAgent, loadResolved]);

  return {
    items,
    selected,
    selectedId,
    setSelectedId,
    loading,
    connectionLost,
    canApprove,
    // Agentes HITL accesibles: grupos fijos del arbol (aunque sin pendientes).
    hitlAgents,
    resume,
    reprocess,
    onResolved,
    count: items.length,
    // Hay mas pendientes de las mostradas (>limite del poll).
    pendingHasMore,
    // Resueltas por agente (bajo demanda + scroll infinito).
    resolvedByAgent,
    resolvedLoading,
    resolvedHasMore,
    loadResolved,
    loadMoreResolved,
    // Registro de actividad por agente (descartes/errores que no generaron tarea).
    activityByAgent,
    activityLoading,
    activityHasMore,
    loadActivity,
    loadMoreActivity,
    // Notificaciones del navegador para nuevas aprobaciones.
    notifPermission,
    requestNotificationPermission,
  };
}
